import { faTriangle } from '@fortawesome/pro-regular-svg-icons/faTriangle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point, polygon } from '@turf/helpers';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Map, { Layer, Marker, Source } from 'react-map-gl';
import { useBoundaryLayers } from '../hooks/useBoundaryLayers';
import { useMapState } from '../hooks/useMapState';
import { useStores } from '../stores/useStores';
import DrawControl from './DrawControl';
import PreviewButton, { monoStyle } from './PreviewButton';

function UpdateTrapsMap({
  updateType,
  trapCoords,
  setTrapCoords,
  trappingZoneName
}) {
  const { traplogStore } = useStores();
  const [mapStyle, setMapStyle] = useState(monoStyle);
  const [boundaryToEdit, setBoundaryToEdit] = useState();
  const drawRef = useRef();
  const [updatedBoundary, setUpdatedBoundary] = useState();
  const layers = useBoundaryLayers([0.2, 0.4, 4, 0.5]);
  const [
    boundaryData,
    ,
    viewport,
    setViewport,
    mapRef,
    mapRefWrapper,
    initialBoundsSet
  ] = useMapState();

  useEffect(() => {
    if (mapRef.current && !trapCoords && initialBoundsSet) {
      const center = mapRef.current.getMap().getCenter();
      setTrapCoords(center);
    }
  }, [mapRef.current]);

  const onMarkerDragStart = useCallback((event) => {
    if (updateType === 'edit') {
      drawRef.current.deleteAll();
      setBoundaryToEdit(undefined);
    }
    setTrapCoords({
      lat: event.lngLat.lat,
      lng: event.lngLat.lng,
      ...trapCoords
    });
  }, []);

  const onMarkerDragEnd = useCallback(
    (event) => {
      setTrapCoords({
        lat: event.lngLat.lat,
        lng: event.lngLat.lng,
        valid: trapCoords.valid,
        currentZone: trapCoords.currentZone
      });
    },
    [trapCoords]
  );

  const onMarkerDrag = useCallback(
    (event) => {
      let isValid = false;
      for (const feature of boundaryData.features) {
        const name = feature.properties.trappingZoneName;
        if (name === trappingZoneName) {
          const coords = feature.geometry.coordinates;
          const pnt = point([event.lngLat.lng, event.lngLat.lat]);
          const poly = polygon(coords);
          if (booleanPointInPolygon(pnt, poly)) {
            isValid = true;
            break;
          }
        } else continue;
      }
      if (isValid)
        setTrapCoords({
          lat: event.lngLat.lat,
          lng: event.lngLat.lng,
          currentZone: trappingZoneName,
          valid: true
        });
      else
        setTrapCoords({
          lat: event.lngLat.lat,
          lng: event.lngLat.lng,
          currentZone: trappingZoneName,
          valid: false
        });
    },
    [trapCoords, boundaryData, trappingZoneName]
  );

  const onMapClick = useCallback(
    (e) => {
      // Only want to allow boundary edits in edit mode.
      if (updateType === 'edit') {
        const clickedFeature = e.features && e.features[0];
        // Boundary clicked so add polygon to map
        if (clickedFeature) {
          const boundary = boundaryData.features.find(
            (element) => element.properties.id === clickedFeature.properties.id
          );
          if (boundaryToEdit?.properties.id !== boundary.properties.id) {
            // First remove the polygon before adding.
            drawRef.current.deleteAll();
            drawRef.current.add(boundary);
            drawRef.current.changeMode('simple_select');
            setBoundaryToEdit(boundary);
          }
          // No boundary clicked so remove the polygon
          // and update if modified.
        } else {
          // Save the modified boundary.
          if (updatedBoundary) {
            const targetZone = traplogStore.trappingZones.find(
              (zone) => zone.id === updatedBoundary.properties.id
            );
            const newZones = traplogStore.trappingZones.filter(
              (zone) => zone.id !== updatedBoundary.properties.id
            );
            targetZone.boundry = JSON.stringify(updatedBoundary.geometry);
            newZones.push(targetZone);
            traplogStore.trappingZones = newZones;
          }
        }
      }
    },
    [boundaryToEdit, updatedBoundary, traplogStore.trappingZones]
  );

  const onEditorUpdate = useCallback((map) => {
    setUpdatedBoundary(map.features[0]);
  }, []);

  return boundaryData.features.length > 0 ? (
    <Map
      ref={mapRefWrapper}
      initialViewState={viewport}
      onViewportChange={(nextViewport) => setViewport(nextViewport)}
      mapStyle={`mapbox://styles/hortplus/${mapStyle}`}
      interactiveLayerIds={[
        'layerStyleThresholdMet',
        'layerStyleNearThreshold',
        'layerStyleGood',
        'layerStyleNoData'
      ]}
      onClick={onMapClick}
      doubleClickZoom={false}
      mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
    >
      <DrawControl
        ref={drawRef}
        initialBoundry={boundaryToEdit}
        position='top-right'
        displayControlsDefault={false}
        defaultMode={boundaryToEdit ? 'simple_select' : 'draw_polygon'}
        onUpdate={onEditorUpdate}
      />
      <Source id='trappingZones' type='geojson' data={boundaryData}>
        {layers.map((layer) => (
          <Layer key={layer.id} {...layer} />
        ))}
      </Source>
      {trapCoords && (
        <Marker
          latitude={trapCoords.lat}
          longitude={trapCoords.lng}
          offsetTop={-18}
          offsetLeft={-8}
          onDragEnd={onMarkerDragEnd}
          onDragStart={onMarkerDragStart}
          onDrag={onMarkerDrag}
          draggable
        >
          <FontAwesomeIcon icon={faTriangle} size={'lg'} />
        </Marker>
      )}
      <PreviewButton
        setMapStyle={setMapStyle}
        mapStyle={mapStyle}
        viewport={viewport}
      />
    </Map>
  ) : (
    <p className='text-warning'>This trapping zone has no bonudary data!</p>
  );
}

UpdateTrapsMap.propTypes = {
  updateType: PropTypes.string,
  trapCoords: PropTypes.object,
  setTrapCoords: PropTypes.func,
  trappingZoneName: PropTypes.string
};

export default observer(UpdateTrapsMap);
